import { useMutation } from "@tanstack/react-query";
import api from "../services/api.ts";
import toast from "react-hot-toast";
import TokenService from "../services/TokenService.ts";

// --- API Functions ---

const loginUser = async (credentials: FormData) => {
    // Interceptor specific logic for login (excludes 401 retry) is handled in api.ts
    // Use api instance to ensure consistent timeouts/config
    const response = await api.post(`${process.env.REACT_APP_DB_URI}/users/user-login/login/`, credentials);
    return response.data;
};

const registerUser = async (userData: FormData) => {
    const response = await api.post(`${process.env.REACT_APP_DB_URI}/users/user-register/`, userData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
    return response.data;
};

const changePassword = async (passwordData: any) => {
    const response = await api.post(`${process.env.REACT_APP_DB_URI}/users/password-management/change-password/`, passwordData);
    return response.data;
};

// --- Hooks ---

export const useLogin = () => {
    return useMutation({
        mutationFn: loginUser,
        onSuccess: (data) => {
            // Token handling is usually done in the component or here. 
            // Login.tsx does it. Let's keep it consistent or move it here.
            // If we move it here, we need to know what 'data' looks like exactly.
            // Login.tsx does: TokenService.setUser(response.data);
            // We'll let the component handle side effects for now to minimize refactor risk, 
            // OR we can do it here. 
            // Let's do it here to centralize auth logic.
            TokenService.setUser(data);
            localStorage.setItem("isAuthenticated", "true");
            toast.success("Login successful!");
        },
        onError: (error: any) => {
            // Error handling logic is complex in Login.tsx. 
            // We can let the component handle it via onError callback passed to mutate, 
            // or just toast here.
            // Login.tsx has specific error messages. 
            // We'll let the component handle UI feedback if it wants, but default toast here.
            // Actually, React Query's useMutation onError does not prevent component's onError from firing if passed to mutate().
            // But usually we prefer one place.
            // I will keep generic toast here, but allow component to do more.
            // Wait, Login.tsx has very specific error parsing.
        }
    });
};

export const useRegister = () => {
    return useMutation({
        mutationFn: registerUser,
        // Success handling in component (redirects, etc)
    });
};

export const useChangePassword = () => {
    return useMutation({
        mutationFn: changePassword,
        onSuccess: () => {
            toast.success("Password changed successfully!");
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message || "Failed to change password");
        }
    });
};
